import {
  TOWERS,
  UNITS,
  defenceBudget,
  towerSlots,
} from "@empire/core-sim";
import {
  FIELDS_PER_RESOURCE,
  buildingStats,
  fieldProduction,
  storageRow,
} from "@empire/shared";

/**
 * What the village screen says about itself (playtest readiness, Sep 2026).
 *
 * The screen used to be seventeen rows of "W 102 C 51" in no useful order, with
 * nothing to say what to do next — the walkthrough's biggest remaining "now
 * what?". Everything here is a pure function of state the client already has,
 * so it can be tested without a screen and never disagrees with the server.
 */

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

export type GroupId = "resources" | "army" | "defence" | "village";

export const GROUPS: readonly { id: GroupId; title: string; buildings: readonly string[] }[] = [
  {
    id: "resources",
    title: "Resources",
    buildings: ["woodcutter", "clay_pit", "iron_mine", "grain_farm", "warehouse", "granary"],
  },
  { id: "army", title: "Army", buildings: ["rally_point", "barracks", "stable", "workshop", "shrine"] },
  { id: "defence", title: "Defence", buildings: ["wall", "engineers_guild"] },
  { id: "village", title: "Village", buildings: ["main_building", "marketplace", "embassy", "residence"] },
];

/** Which group a building sits in. Anything the list forgot goes under Village. */
export function groupOf(buildingId: string): GroupId {
  return GROUPS.find((g) => g.buildings.includes(buildingId))?.id ?? "village";
}

// ---------------------------------------------------------------------------
// What the next level gives you
// ---------------------------------------------------------------------------

const RESOURCE_NAME: Record<string, string> = { wood: "wood", clay: "clay", iron: "iron", grain: "grain" };

/**
 * One short line: what building `id` from `level` to `level + 1` buys.
 *
 * Computed from the same tables the server uses where a number exists (fields,
 * storage, Wall, Guild), and from the sheet's own wording where it does not.
 * "See Fields sheet" is what the sheet says for a field — true for a designer,
 * useless on a phone.
 */
export function nextGain(id: string, level: number): string {
  const next = level + 1;
  const stats = buildingStats(id);

  if (stats.produces) {
    const gain = FIELDS_PER_RESOURCE * (fieldProduction(next) - fieldProduction(level));
    return `+${gain} ${RESOURCE_NAME[stats.produces]}/h`;
  }

  switch (id) {
    case "warehouse":
    case "granary": {
      const cap = storageRow(next).capacity;
      const what = id === "granary" ? "grain" : "wood, clay, iron";
      return `Holds ${cap.toLocaleString()} ${what}`;
    }
    case "wall": {
      const now = defenceBudget(level);
      const then = defenceBudget(next);
      const parts = [`+${then.roadTiles - now.roadTiles} road tiles`];
      if (then.mudTiles > now.mudTiles) parts.push("+1 mud");
      if (then.barricades > now.barricades) parts.push("+1 barricade");
      parts.push("tougher towers");
      return parts.join(", ");
    }
    case "engineers_guild": {
      const parts: string[] = [];
      if (towerSlots(next) > towerSlots(level)) parts.push("+1 tower slot");
      for (const tower of TOWERS) {
        if (tower.unlockEngineersGuild === next) parts.push(`unlocks ${tower.name}`);
      }
      if (next === 5) parts.push("towers to level 2");
      if (next === 12) parts.push("towers to level 3");
      return parts.length > 0 ? parts.join(", ") : "Closer to the next tower";
    }
    case "barracks": {
      const unlocked = UNITS.filter((u) => u.trainedIn?.building === "barracks" && u.trainedIn.level === next);
      const parts = ["4% faster training"];
      for (const unit of unlocked) parts.unshift(`unlocks ${unit.name}`);
      return parts.join(", ");
    }
    case "stable":
    case "workshop":
    case "shrine": {
      const unlocked = UNITS.filter((u) => u.trainedIn?.building === id && u.trainedIn.level === next);
      return unlocked.length > 0
        ? `Unlocks ${unlocked.map((u) => u.name).join(" and ")}`
        : "4% faster training";
    }
    case "main_building":
      return next === 10 ? "A second builder" : "Builds 3% faster";
    case "rally_point":
      return "+20 units per attack";
    case "embassy":
      return next === 1 ? "Join an alliance" : next === 3 ? "Found your own alliance" : "+5 alliance members";
    case "marketplace":
      return "+1 merchant (carries 500)";
    case "residence":
      return next % 10 === 0 ? "Trains 3 Settlers" : "+2 culture per hour";
    default:
      // Spreadsheet notes like "(cap Assumptions)" point at the balance file, not the player.
      return stats.perLevelEffect.replace(/\s*\((?:capped by|cap) Assumptions\)/giu, "");
  }
}

// ---------------------------------------------------------------------------
// The next step — the chain itself lives in @empire/shared (29 Sep 2026), so
// the server can check a step before paying its reward.
// ---------------------------------------------------------------------------

export {
  GOALS,
  currentGoal,
  goalReward,
  type Goal,
  type GoalAction,
  type GoalContext,
} from "@empire/shared";
